import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../Config/api';
import isPasswordValid from '../Utils/isPasswordValid';
import { Icon } from '../Components/UI/Icon';
import PasswordRequirements from '../Components/PasswordRequirements';
import { fetchWithAuth } from '../Utils/fetchWithAuth';

export default function ChangePassword() {
    const navigate = useNavigate();

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (newPassword !== confirm) {
            setErrorMsg('Le nuove password non coincidono');
            return;
        }

        if (!isPasswordValid(newPassword)) {
            setErrorMsg('La nuova password non soddisfa i requisiti di sicurezza');
            return;
        }

        setLoading(true);

        try {
            const res = await fetchWithAuth(API_ENDPOINTS.changePassword, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({
                    old_password: oldPassword,
                    new_password: newPassword
                }),
            });

            if (res.ok) {
                setSuccess(true);
                setTimeout(() => navigate('/'), 1500);
            } else {
                const data = await res.json();
                setErrorMsg(data.detail || 'Errore generico');
            }
        } catch (e) {
            setErrorMsg('Errore di rete');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-xl flex flex-col items-center gap-3">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="self-start flex gap-2 items-center group text-gray-800 transition"
                >
                    <Icon name="longarrowleft" />
                    <span className="relative font-medium after:absolute after:left-0 after:bottom-0 after:h-[1.5px] after:w-0 after:bg-gray-800 after:transition-all after:duration-300 group-hover:after:w-full">
                        Torna alla dashboard
                    </span>
                </button>


                <form
                    onSubmit={handleSubmit}
                    className="bg-white p-8 rounded shadow-md space-y-6 relative w-full"
                >
                    <h2 className="text-xl font-bold text-gray-800">Cambia la tua password</h2>

                    <div className="flex flex-col md:flex-row gap-14">
                        <div className="space-y-4 w-full md:w-1/2">
                            {/* Vecchia password */}
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Vecchia password</label>
                                <div className="relative">
                                    <input
                                        type={showOld ? 'text' : 'password'}
                                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300 pr-10"
                                        value={oldPassword}
                                        onChange={(e) => setOldPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowOld(prev => !prev)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                    >
                                        <Icon name={showOld ? 'eye' : 'eyeslash'} />
                                    </button>
                                </div>
                            </div>

                            {/* Nuova password */}
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Nuova password</label>
                                <div className="relative">
                                    <input
                                        type={showNew ? 'text' : 'password'}
                                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300 pr-10"
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value);
                                            if (errorMsg) setErrorMsg('');
                                        }}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNew(prev => !prev)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                    >
                                        <Icon name={showNew ? 'eye' : 'eyeslash'} />
                                    </button>
                                </div>
                            </div>

                            {/* Conferma nuova password */}
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Conferma nuova password</label>
                                <div className="relative">
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300 pr-10"
                                        value={confirm}
                                        onChange={(e) => {
                                            setConfirm(e.target.value);
                                            if (errorMsg) setErrorMsg('');
                                        }}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(prev => !prev)}
                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                    >
                                        <Icon name={showConfirm ? 'eye' : 'eyeslash'} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Requisiti password */}
                        <PasswordRequirements password={newPassword} />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded disabled:opacity-50"
                    >
                        {loading ? 'Salvataggio...' : 'Conferma'}
                    </button>

                    {errorMsg && (
                        <p className="text-red-600 absolute -bottom-7 left-0 right-0 text-sm text-center">
                            {errorMsg}
                        </p>
                    )}
                    {success && (
                        <p className="text-green-600 absolute -bottom-7 left-0 right-0 text-sm text-center">
                            Password aggiornata con successo!
                        </p>
                    )}
                </form>
            </div>
        </div>
    );
}
